import { Language, Outcome, Province, Race, UserRole, type LanguageEnum, type OutcomeEnum, type ProvinceEnum, type RaceEnum, type UserRoleEnum } from "#shared/enums";

/**
 * Because we are deploying an SSR static web app, i18n does not work.
 * 
 * This is a workaround to provide the same functionality.
 */
export const Text = {
 Language: {
  [Language.AF]: 'Afrikaans',
  [Language.EN]: 'English',
  [Language.NR]: 'Ndebele',
  [Language.SE]: 'Sepedi',
  [Language.ST]: 'Sotho',
  [Language.SS]: 'Swati',
  [Language.TS]: 'Tsonga',
  [Language.TN]: 'Tswana',
  [Language.VE]: 'Venda',
  [Language.XH]: 'Xhosa',
  [Language.ZU]: 'Zulu',
 },
 Outcome: {
  [Outcome.NOT_STARTED]: 'Not Started',
  [Outcome.STARTED]: 'Started',
  [Outcome.ABSENT]: 'Absent',
  [Outcome.CANCELLED]: 'Cancelled',
  [Outcome.RESCHEDULED]: 'Rescheduled',
  [Outcome.PASS]: 'Pass',
  [Outcome.FAIL]: 'Fail'
 },
 Province: {
  [Province.WESTERN_CAPE]: 'Western Cape',
  [Province.EASTERN_CAPE]: 'Eastern Cape',
  [Province.NORTHERN_CAPE]: 'Northern Cape',
  [Province.FREE_STATE]: 'Free State',
  [Province.GAUTENG]: 'Gauteng',
  [Province.MPUMALANGA]: 'Mpumalanga',
  [Province.LIMPOPO]: 'Limpopo',
  [Province.KWAZULU_NATAL]: 'KwaZulu-Natal',
  [Province.NORTH_WEST]: 'North West'
 },
 Race: {
  [Race.ASIAN_OR_INDIAN]: 'Asian or Indian',
  [Race.BLACK_AFRICAN]: 'Black African',
  [Race.COLOURED]: 'Coloured',
  [Race.WHITE]: 'White'
 },
 Role: {
  [UserRole.ADMIN]: 'Administrator',
  [UserRole.DEMO]: 'Demo',
  [UserRole.INSPECTOR]: 'Inspector',
  [UserRole.PARENT]: 'Parent',
  [UserRole.STUDENT]: 'Student',
  [UserRole.SUPERVISOR]: 'Supervisor',
  [UserRole.TESTER]: 'Tester',
 },
 Assessment: {
  ReadingPassage: {
    [Language.AF]: 'Lees die volgende en beantwoord dan die vrae',
    [Language.EN]: 'Read the following and then answer the questions',
    [Language.NR]: 'Funda okulandelayo bese uphendula imibuzo',
    [Language.SE]: 'Bala tše di latelago gomme o arabe dipotšišo',
    [Language.ST]: 'Bala tse latelang ebe o araba dipotso',
    [Language.SS]: 'Funda lokulandelako bese uphendvula imibuto',
    [Language.TS]: 'Hlaya leswi landzelaka ivi u hlamula swivutiso',
    [Language.TN]: 'Bala tse di latelang mme o arabe dipotso',
    [Language.VE]: 'Vhalani zwi tevhelaho vho dovha vho phindula mbudziso',
    [Language.XH]: 'Funda oku kulandelayo uze uphendule imibuzo',
    [Language.ZU]: 'Funda okulandelayo bese uphendula imibuzo'
  },
  PartXOfY: {
    [Language.AF]: 'Deel {0} van {1}',
    [Language.EN]: 'Part {0} of {1}',
    [Language.NR]: 'Ingxenye {0} ye- {1}',
    [Language.SE]: 'Karolo {0} ya {1}',
    [Language.ST]: 'Karolo {0} ya {1}',
    [Language.SS]: 'Incenye {0} ye- {1}',
    [Language.TS]: 'Xiphemu {0} xa {1}',
    [Language.TN]: 'Karolo {0} ya {1}',
    [Language.VE]: 'Tshipida {0} tsha {1}',
    [Language.XH]: 'Inxalenye {0} ye- {1}',
    [Language.ZU]: 'Ingxenye {0} ye- {1}'
  },
  SelectAllThatApply: {
   [Language.AF]: 'Kies alles wat van toepassing is:',
   [Language.EN]: 'Select all that apply:',
   [Language.NR]: 'Khetha konke okusebenzayo:',
   [Language.SE]: 'Kgetha tšohle tšeo di šomago:',
   [Language.ST]: 'Khetha tsohle tse sebetsang:',
   [Language.SS]: 'Khetsa konke lokusetjentiswa:',
   [Language.TS]: 'Hlawula hinkwaswo leswi tirhanaka:',
   [Language.TN]: 'Tlhopha tsotlhe tse di berekang:',
   [Language.VE]: 'Nangani zwothe zwi shumaho:',
   [Language.XH]: 'Khetha konke okusebenzayo:',
   [Language.ZU]: 'Khetha konke okusebenzayo:'
  },
  SelectOneThatApply: {
    [Language.AF]: 'Kies die korrekte antwoord:',
    [Language.EN]: 'Select the correct answer:',
    [Language.NR]: 'Khetha impendulo efanelekile:',
    [Language.SE]: 'Kgetha karabo ye nepagetšego:',
    [Language.ST]: 'Khetha karabo e nepahetseng:',
    [Language.SS]: 'Khetsa impendvulo lepholo:',
    [Language.TS]: 'Hlawula nhlamulo lowu lulameke:',
    [Language.TN]: 'Tlhopha karabo e e siameng:',
    [Language.VE]: 'Nangani phindulo yo teaho:',
    [Language.XH]: 'Khetha impendulo echanekileyo:',
    [Language.ZU]: 'Khetha impendulo elungile:'
  }
 },
 UNSPECIFIED: 'Unspecified'
} as const;

// Note: Cities data is dynamically loaded from postal code files
// This is a placeholder for the expected structure
export const CITIES: Record<string, string> = {} as const;

// Type helpers for better type safety
export type LanguageCode = keyof typeof Text.Language;
export type OutcomeCode = keyof typeof Text.Outcome;
export type ProvinceCode = keyof typeof Text.Province;
export type RaceCode = keyof typeof Text.Race;
export type RoleCode = keyof typeof Text.Role;

// Utility functions for lookups
export const translateLanguage = (code: LanguageEnum | null | undefined): string => {
  return Text.Language[code as LanguageEnum] || Text.UNSPECIFIED;
};

export const translateOutcome = (code: OutcomeEnum | null | undefined): string => {
  return Text.Outcome[code as OutcomeEnum] || Text.UNSPECIFIED;
};

export const translateProvince = (code: ProvinceEnum | null | undefined): string => {
  return Text.Province[code as ProvinceEnum] || Text.UNSPECIFIED;
};

export const translateRace = (code: RaceEnum | null | undefined): string => {
  return Text.Race[code as RaceEnum] || Text.UNSPECIFIED;
};

export const translateRole = (code: UserRoleEnum | null | undefined): string => {
  return Text.Role[code as UserRoleEnum] || Text.UNSPECIFIED;
};

export const translateAssessmentText = (key: keyof typeof Text.Assessment, language: LanguageEnum | null | undefined): string => {
  const assessmentTexts = Text.Assessment[key];
  return assessmentTexts[language as keyof typeof assessmentTexts] || assessmentTexts[Language.EN];
};

export const translatePartXOfY = (partNumber: number, totalParts: number, language: LanguageEnum | null | undefined): string => {
  const template = translateAssessmentText('PartXOfY', language);
  return template.replace('{0}', partNumber.toString()).replace('{1}', totalParts.toString());
};

// Array functions for form options
export const getLanguagesAsArray = () => {
  return Object.entries(Text.Language).map(([key, value]) => ({
    value: key,
    text: value
  }));
};
